import { useCallback, useEffect, useRef } from 'react';

type PollingTask = (signal: AbortSignal) => Promise<void> | void;

interface SmartPollingOptions {
    intervalMs: number;
    enabled?: boolean;
    immediate?: boolean;
    jitterRatio?: number;
    maxBackoffMs?: number;
    restartKey?: unknown;
    runWhenHidden?: boolean;
}

interface SmartPollingResult {
    refreshNow: () => void;
}

const EVENT_DEDUPE_MS = 1000;

function jitteredDelay(baseMs: number, jitterRatio: number): number {
    const safeRatio = Math.max(0, Math.min(jitterRatio, 0.5));
    const spread = baseMs * safeRatio;
    return Math.max(1000, Math.round(baseMs - spread + Math.random() * spread * 2));
}

/**
 * Visibility-aware polling with one request at a time. The next timeout is
 * scheduled only after the current task finishes, so slow responses can never
 * create an overlapping request chain.
 */
export function useSmartPolling(
    task: PollingTask,
    options: SmartPollingOptions,
): SmartPollingResult {
    const taskRef = useRef(task);
    const executeRef = useRef<((source: 'manual' | 'event') => void) | null>(null);
    taskRef.current = task;

    const {
        intervalMs,
        enabled = true,
        immediate = true,
        jitterRatio = 0.1,
        maxBackoffMs = Math.max(intervalMs * 4, intervalMs),
        restartKey,
        runWhenHidden = false,
    } = options;

    useEffect(() => {
        if (!enabled) {
            executeRef.current = null;
            return;
        }

        let destroyed = false;
        let timeoutId: number | null = null;
        let controller: AbortController | null = null;
        let running = false;
        let failures = 0;
        let lastEventRunAt = 0;

        const isHidden = () => typeof document !== 'undefined' && document.hidden;

        const clearTimer = () => {
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
                timeoutId = null;
            }
        };

        const schedule = () => {
            clearTimer();
            if (destroyed || (!runWhenHidden && isHidden())) return;
            const backoffBase = Math.min(intervalMs * (2 ** failures), maxBackoffMs);
            timeoutId = window.setTimeout(() => {
                void execute('timer');
            }, jitteredDelay(backoffBase, jitterRatio));
        };

        const execute = async (source: 'timer' | 'manual' | 'event') => {
            if (destroyed || running) return;
            if (!runWhenHidden && isHidden() && source === 'timer') return;

            if (source === 'event') {
                const now = Date.now();
                if (now - lastEventRunAt < EVENT_DEDUPE_MS) return;
                lastEventRunAt = now;
            }

            clearTimer();
            running = true;
            controller = new AbortController();
            try {
                await taskRef.current(controller.signal);
                failures = 0;
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    failures += 1;
                }
            } finally {
                running = false;
                controller = null;
                if (!destroyed) schedule();
            }
        };

        const runFromEvent = () => {
            if (!runWhenHidden && isHidden()) {
                clearTimer();
                return;
            }
            void execute('event');
        };
        const onVisibilityChange = () => {
            if (isHidden()) {
                clearTimer();
                return;
            }
            runFromEvent();
        };

        executeRef.current = (source) => {
            void execute(source);
        };
        window.addEventListener('focus', runFromEvent);
        window.addEventListener('online', runFromEvent);
        document.addEventListener('visibilitychange', onVisibilityChange);

        if (immediate && (runWhenHidden || !isHidden())) {
            void execute('manual');
        } else {
            schedule();
        }

        return () => {
            destroyed = true;
            executeRef.current = null;
            clearTimer();
            controller?.abort();
            window.removeEventListener('focus', runFromEvent);
            window.removeEventListener('online', runFromEvent);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [enabled, immediate, intervalMs, jitterRatio, maxBackoffMs, restartKey, runWhenHidden]);

    const refreshNow = useCallback(() => {
        executeRef.current?.('manual');
    }, []);

    return { refreshNow };
}
